import { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import type { KybStatus } from '../../../api/src/types/database';
import { createManagementActions } from './createManagementStore';

export interface KybSubmissionDetail {
  id: string;
  status: KybStatus;
  submittedAt?: string;
  reviewStartedAt?: string;
  reviewCompletedAt?: string;
  updatedAt?: string;
  rejectionReason?: string;
  additionalInfoRequested?: string;
  sumsubApplicantId?: string;
  publisherUserJoinId: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    onboardingState: string;
    emailVerified: boolean;
    created: string;
  };
  publisher: {
    type: 'Organization' | 'Individual';
    name: string;
    walletAddress?: string;
    nftMintAddress?: string;
    website?: string;
  };
  sumsubApplicant?: {
    id: string;
    sumsubId: string;
    externalUserId?: string;
    inspectionId?: string;
    correlationId?: string;
    email?: string;
    phone?: string;
    reviewStatus?: string;
    type?: string;
    companyName?: string;
    sumsubCreatedAt?: string;
    sumsubUpdatedAt?: string;
    lastSyncedAt?: string;
    reviewResult?: unknown;
    properties?: unknown;
  };
  syncStatus?: {
    lastSyncTimestamp?: string;
    lastSyncStatus?: string;
    syncHealth: 'healthy' | 'warning' | 'error' | 'unknown';
    errorMessage?: string;
  };
  stats: {
    totalEvents: number;
    lastEventAt?: string;
    moduleResults: {
      GREEN: number;
      YELLOW: number;
      RED: number;
    };
  };
}

interface KybManagementState {
  submissions: KybSubmissionDetail[];
  filters: {
    status: KybStatus | 'all';
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  summary: {
    totalByStatus: Record<KybStatus, number>;
    approvalRate: number;
    averageProcessingTime?: number;
  } | null;
  ui: {
    isLoading: boolean;
    selectedSubmissionId: string | null;
  };
}

const initialFilters: KybManagementState['filters'] = {
  status: 'all',
  search: '',
};

const defaultSorting: SortingState = [{ id: 'submittedAt', desc: true }];

export const kybManagementStore = new Store<KybManagementState>({
  submissions: [],
  filters: initialFilters,
  sorting: defaultSorting,
  pagination: { currentPage: 1, totalPages: 1, total: 0 },
  summary: null,
  ui: { isLoading: true, selectedSubmissionId: null },
});

const actions = createManagementActions(kybManagementStore, {
  initialFilters,
  defaultSorting,
});

export const {
  updateFilter,
  setSorting,
  setPage,
  setPaginationInfo,
  setLoading,
} = actions;

export const setSubmissions = (submissions: KybSubmissionDetail[]) => {
  kybManagementStore.setState((prev) => ({ ...prev, submissions }));
};

export const setSummary = (summary: KybManagementState['summary']) => {
  kybManagementStore.setState((prev) => ({ ...prev, summary }));
};

export const selectSubmission = (submissionId: string | null) => {
  actions.setUiField('selectedSubmissionId', submissionId);
};
